.button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--tempest-control-gap, var(--tempest-space-2));
    border: 1px solid transparent;
    border-radius: var(--tempest-control-radius, var(--tempest-radius-md));
    font-family: var(--tempest-font-sans);
    font-weight: var(--tempest-weight-semibold);
    line-height: var(--tempest-leading-none);
    letter-spacing: var(--tempest-tracking-normal);
    text-decoration: none;
    white-space: nowrap;
    user-select: none;
    transition: var(--tempest-transition-base);
    box-shadow: var(--tempest-shadow-xs);
}

.button:focus-visible {
    outline: var(--tempest-focus-ring-width) solid var(--tempest-focus-ring-color);
    outline-offset: var(--tempest-focus-ring-offset);
}

@media (hover: hover) and (pointer: fine) {
    .button:hover:not(:disabled) {
        box-shadow: var(--tempest-shadow-sm);
    }
}

.button:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: var(--tempest-shadow-xs);
}

.button:disabled {
    opacity: 0.55;
    box-shadow: none;
}

/* ============================================================
 * Variants — solid (default), soft, outline, ghost
 * Status colors: primary, secondary (neutral), danger, success
 * ============================================================ */

/* primary — solid filled */
.primary {
    background-color: var(--tempest-primary);
    color: var(--tempest-primary-foreground);
}
.primary:hover:not(:disabled) {
    background-color: var(--tempest-primary-hover);
}
.primary:active:not(:disabled) {
    background-color: var(--tempest-primary-active);
}

/* secondary — neutral surface */
.secondary {
    background-color: var(--tempest-surface);
    color: var(--tempest-text);
    border-color: var(--tempest-border);
}
.secondary:hover:not(:disabled) {
    background-color: var(--tempest-surface-2);
    border-color: var(--tempest-border-strong);
}

/* danger — solid red */
.danger {
    background-color: var(--tempest-danger-solid);
    color: var(--tempest-danger-on-solid);
}
.danger:hover:not(:disabled) {
    background-color: var(--tempest-danger-hover);
}

/* success — solid green */
.success {
    background-color: var(--tempest-success-solid);
    color: var(--tempest-success-on-solid);
}
.success:hover:not(:disabled) {
    filter: brightness(0.92);
}

/* ghost — transparent, hover surface */
.ghost {
    background-color: transparent;
    color: var(--tempest-text);
    box-shadow: none;
}
.ghost:hover:not(:disabled) {
    background-color: var(--tempest-surface);
    box-shadow: none;
}
.ghost:active:not(:disabled) {
    background-color: var(--tempest-surface-2);
    box-shadow: none;
}

/* soft — tinted background, primary text */
.soft {
    background-color: var(--tempest-primary-soft);
    color: var(--tempest-primary-on-soft);
    box-shadow: none;
}
.soft:hover:not(:disabled) {
    background-color: var(--tempest-primary-soft-hover);
}

/* outline — bordered, transparent fill */
.outline {
    background-color: transparent;
    color: var(--tempest-primary);
    border-color: var(--tempest-primary);
    box-shadow: none;
}
/*
 * The text colour has to move with the background.
 *
 * `.outline` sets `color: var(--tempest-primary)` over a transparent background,
 * where it is fine. This rule tints the background to `--tempest-primary-soft` and,
 * without restating the colour, leaves the fill token sitting on the tint: **4.38:1 in
 * the light theme and 4.28:1 in dark**, both under the 4.5:1 floor for text — so the
 * label got harder to read at the exact moment the pointer was on it.
 *
 * `--tempest-primary-on-soft` is the token for this ground (6.18:1 / 8.72:1) and
 * `docs/styles.md` has documented the rule all along. A rule that changes only the
 * background of a coloured element is the shape of this bug; the other eight places in
 * the SDK that tint a background inherit `--tempest-text`, `--tempest-text-muted` or
 * `--tempest-primary-on-soft` and are fine.
 */
.outline:hover:not(:disabled) {
    background-color: var(--tempest-primary-soft);
    color: var(--tempest-primary-on-soft);
}

/* link — text-only, underline on hover */
.link {
    background-color: transparent;
    color: var(--tempest-primary);
    box-shadow: none;
    padding-left: 0;
    padding-right: 0;
    border-radius: var(--tempest-radius-sm);
}
.link:hover:not(:disabled) {
    color: var(--tempest-primary-hover);
    text-decoration: underline;
    background: none;
    box-shadow: none;
}

/* ============================================================
 * Sizes — token-driven, density-aware
 * ============================================================ */
.xs {
    height: var(--tempest-control-height-xs);
    padding: 0 var(--tempest-control-padding-xs);
    font-size: var(--tempest-control-font-xs);
}
.sm {
    height: var(--tempest-control-height-sm);
    padding: 0 var(--tempest-control-padding-sm);
    font-size: var(--tempest-control-font-sm);
}
.md {
    height: var(--tempest-control-height-md);
    padding: 0 var(--tempest-control-padding-md);
    font-size: var(--tempest-control-font-md);
}
.lg {
    height: var(--tempest-control-height-lg);
    padding: 0 var(--tempest-control-padding-lg);
    font-size: var(--tempest-control-font-lg);
}
.xl {
    height: var(--tempest-control-height-xl);
    padding: 0 var(--tempest-control-padding-xl);
    font-size: var(--tempest-control-font-xl);
}

/* Square (icon-only) — width matches height */
.iconOnly {
    position: relative;
}
.iconOnly.xs {
    width: var(--tempest-control-height-xs);
    padding: 0;
}
.iconOnly.sm {
    width: var(--tempest-control-height-sm);
    padding: 0;
}
.iconOnly.md {
    width: var(--tempest-control-height-md);
    padding: 0;
}
.iconOnly.lg {
    width: var(--tempest-control-height-lg);
    padding: 0;
}
.iconOnly.xl {
    width: var(--tempest-control-height-xl);
    padding: 0;
}

/* Touch hit-slop — extend tap target to a min 44×44 on coarse pointers
 * for the smallest icon-only sizes, without changing visual footprint. */
@media (pointer: coarse) {
    .iconOnly.xs::after,
    .iconOnly.sm::after {
        content: "";
        position: absolute;
        inset: -8px;
    }
}

/* Pill shape */
.pill {
    border-radius: var(--tempest-radius-full);
}

.fullWidth {
    width: 100%;
}

/*
 * Lay out leftIcon + label + rightIcon as one centered, gapped row. Without
 * this the three are inline inside a single span (no gap, icon baseline-
 * aligned instead of centered), which reads as off-center — most visible on
 * fullWidth buttons where the label has room to drift.
 */
.content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--tempest-control-gap, var(--tempest-space-2));
    min-width: 0;
}

.loading .hiddenText {
    visibility: hidden;
}

.spinner {
    position: absolute;
    animation: tempest-spin 0.8s linear infinite;
}

@keyframes tempest-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
